package org.multiplatform.kotfolio.pages.kmm

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.core.Page

@Page("/kmm/ns_user_defaults")
@Composable
fun KMMNSUserDefaultsPage() {
    BlogLayout(
        PageProperties(
            "NSUserDefaults",
            ""
        ),
        BlogProperties("Kotlin Multiplatform", "NSUserDefaults")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            KotlinCode(
                code = """
                    //common
                    import kotlin.enums.EnumEntries
                    import kotlin.enums.enumEntries

                    interface IntPersistable {
                        val number: Int

                        companion object {
                            fun <E> fromPersistence(
                                number: Int,
                                entries: EnumEntries<E>
                            ): E? where E : Enum<E>, E : IntPersistable {
                                return entries.find { it.number == number }
                            }

                            inline fun <reified E> fromPersistence(
                                number: Int
                            ): E? where E : Enum<E>, E : IntPersistable {
                                return fromPersistence(number, enumEntries())
                            }

                            inline fun <reified E> createBitmask(
                                values: Collection<E>
                            ): Int where E : Enum<E>, E : IntPersistable {
                                return values.fold(0) { acc, e -> acc or (1 shl e.number) }
                            }

                            inline fun <reified E> fromBitmask(
                                mask: Int
                            ): Set<E> where E : Enum<E>, E : IntPersistable {
                                return enumEntries<E>().filter { (1 shl it.number) and mask != 0 }.toSet()
                            }
                        }
                    }
                         
                    import kotlinx.datetime.Instant
                    import kotlinx.serialization.encodeToString
                    import kotlinx.serialization.json.Json

                    interface Preferences {
                        operator fun <T> set(key: PreferencesKey<T>, value: T?)

                        operator fun <T> get(key: PreferencesKey<T>): T?

                        fun clear()
                    }

                    expect fun Preferences(): Preferences

                    sealed interface PreferencesKey<T> {
                        val key: String
                    }

                    data class IntPreferencesKey(override val key: String) : PreferencesKey<Int>

                    data class LongPreferencesKey(override val key: String) : PreferencesKey<Long>

                    data class FloatPreferencesKey(override val key: String) : PreferencesKey<Float>

                    data class BooleanPreferencesKey(override val key: String) : PreferencesKey<Boolean>

                    data class StringPreferencesKey(override val key: String) : PreferencesKey<String>

                    class BooleanPreferenceDelegate(private val key: BooleanPreferencesKey) {
                        private val preferences: Preferences = Preferences()

                        operator fun getValue(thisRef: Any?, property: Any?): Boolean? {
                            return preferences[key]
                        }

                        operator fun setValue(thisRef: Any?, property: Any?, value: Boolean?) {
                            preferences[key] = value
                        }
                    }

                    fun persisting(
                        key: BooleanPreferencesKey
                    ): BooleanPreferenceDelegate = BooleanPreferenceDelegate(key)

                    class IntPreferenceDelegate(private val key: IntPreferencesKey) {
                        private val preferences: Preferences = Preferences()

                        operator fun getValue(thisRef: Any?, property: Any?): Int? {
                            return preferences[key]
                        }

                        operator fun setValue(thisRef: Any?, property: Any?, value: Int?) {
                            preferences[key] = value
                        }
                    }

                    fun persisting(key: IntPreferencesKey): IntPreferenceDelegate = IntPreferenceDelegate(key)

                    class LongPreferenceDelegate(private val key: LongPreferencesKey) {
                        private val preferences: Preferences = Preferences()

                        operator fun getValue(thisRef: Any?, property: Any?): Long? {
                            return preferences[key]
                        }

                        operator fun setValue(thisRef: Any?, property: Any?, value: Long?) {
                            preferences[key] = value
                        }
                    }

                    fun persisting(key: StringPreferencesKey): StringPreferenceDelegate = StringPreferenceDelegate(key)

                    class StringPreferenceDelegate(private val key: StringPreferencesKey) {
                        private val preferences: Preferences = Preferences()

                        operator fun getValue(thisRef: Any?, property: Any?): String? {
                            return preferences[key]
                        }

                        operator fun setValue(thisRef: Any?, property: Any?, value: String?) {
                            preferences[key] = value
                        }
                    }

                    fun persisting(key: LongPreferencesKey): LongPreferenceDelegate = LongPreferenceDelegate(key)

                    // stores a list of strings as a json array
                    class StringListPreferenceDelegate(private val key: StringPreferencesKey) {
                        private val preferences: Preferences = Preferences()

                        operator fun getValue(thisRef: Any?, property: Any?): List<String>? {
                            return preferences[key]?.let { json -> Json.decodeFromString(json) }
                        }

                        operator fun setValue(thisRef: Any?, property: Any?, value: List<String>?) {
                            if (value == null) {
                                preferences[key] = null
                                return
                            }

                            val json = Json.encodeToString(value)
                            preferences[key] = json
                        }
                    }

                    fun persistingList(key: StringPreferencesKey): StringListPreferenceDelegate =
                        StringListPreferenceDelegate(key)

                    fun persistingInstant(key: String): InstantPreferenceDelegate =
                        InstantPreferenceDelegate(LongPreferencesKey(key))

                    class InstantPreferenceDelegate(private val key: LongPreferencesKey) {
                        private val preferences: Preferences = Preferences()

                        operator fun getValue(thisRef: Any?, property: Any?): Instant? {
                            return preferences[key]?.let { Instant.fromEpochMilliseconds(it) }
                        }

                        operator fun setValue(thisRef: Any?, property: Any?, value: Instant?) {
                            if (value == null) {
                                preferences[key] = null
                                return
                            }

                            preferences[key] = value.toEpochMilliseconds()
                        }
                    }
                      
                    //androidMain
                    import android.content.Context
                    import android.content.SharedPreferences

                    object AndroidPreferences : Preferences {
                        private val preferences: SharedPreferences
                            get() = PathApplication.instance.getSharedPreferences("path", Context.MODE_PRIVATE)

                        override fun <T> set(key: PreferencesKey<T>, value: T?) {
                            preferences.edit()
                                .apply {
                                    if (value == null) {
                                        remove(key.key)
                                        return@apply
                                    }

                                    when (key) {
                                        is IntPreferencesKey -> putInt(key.key, value as Int)
                                        is LongPreferencesKey -> putLong(key.key, value as Long)
                                        is FloatPreferencesKey -> putFloat(key.key, value as Float)
                                        is BooleanPreferencesKey -> putBoolean(key.key, value as Boolean)
                                        is StringPreferencesKey -> putString(key.key, value as String)
                                    }
                                }
                                .apply()
                        }

                        override fun <T> get(key: PreferencesKey<T>): T? {
                            if (!preferences.contains(key.key)) return null
                            return when (key) {
                                is IntPreferencesKey -> preferences.getInt(key.key, Int.MIN_VALUE)
                                is FloatPreferencesKey -> preferences.getFloat(key.key, Float.MIN_VALUE)
                                is LongPreferencesKey -> preferences.getLong(key.key, Long.MIN_VALUE)
                                is BooleanPreferencesKey -> preferences.getBoolean(key.key, false)
                                is StringPreferencesKey -> preferences.getString(key.key, null)
                            } as T
                        }

                        override fun clear() {
                            preferences.edit().clear().apply()
                        }
                    }

                    var testInstance: Preferences? = null

                    actual fun Preferences(): Preferences {
                        return testInstance ?: AndroidPreferences
                    }
                        
                    //iosMain
                    import platform.Foundation.NSUserDefaults

                    object IosPreferences : Preferences {
                        private val userDefaults = NSUserDefaults.standardUserDefaults

                        override fun <T> set(key: PreferencesKey<T>, value: T?) {
                            if (value == null) {
                                userDefaults.removeObjectForKey(key.key)
                                return
                            }

                            when (key) {
                                is IntPreferencesKey -> userDefaults.setInteger((value as Int).toLong(), key.key)
                                is LongPreferencesKey -> userDefaults.setInteger(value as Long, key.key)
                                is FloatPreferencesKey -> userDefaults.setFloat(value as Float, key.key)
                                is BooleanPreferencesKey -> userDefaults.setBool(value as Boolean, key.key)
                                is StringPreferencesKey -> userDefaults.setObject(value as String, key.key)
                            }
                        }

                        override fun <T> get(key: PreferencesKey<T>): T? {
                            if (userDefaults.objectForKey(key.key) == null) return null
                            return when (key) {
                                is IntPreferencesKey -> userDefaults.integerForKey(key.key).toInt() as T
                                is FloatPreferencesKey -> userDefaults.floatForKey(key.key) as T
                                is LongPreferencesKey -> userDefaults.integerForKey(key.key) as T
                                is BooleanPreferencesKey -> userDefaults.boolForKey(key.key) as T
                                is StringPreferencesKey -> userDefaults.stringForKey(key.key) as T
                            }
                        }

                        override fun clear() {
                            userDefaults.dictionaryRepresentation().keys.forEach {
                                if (it !is String) return@forEach
                                userDefaults.removeObjectForKey(it)
                            }
                        }
                    }

                    actual fun Preferences(): Preferences = IosPreferences
                    """.trimIndent()
            )
        }
    }
}